import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  GetDashboardByTokenDocument,
  ListAnalysesDocument,
  UpdateAnalysisMutationOptions,
  useUpdateAnalysisMutation,
} from 'generated/graphql';
import { DocumentNode } from 'graphql';
import { useEffect } from 'react';
import { Analysis } from 'v2/core/entities/analysis';
import { AndOfErrors } from 'v2/core/errors/errors';
import { mapToScope } from 'v2/core/repository/edge-server/analysis/toEdgeAnalysisMappers';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import useGetAnalysisDeps from 'v2/core/services/analysis/useGetAnalysisDeps';

export const UPDATE_ANALYSIS_REFETCH_QUERIES = [
  ListAnalysesDocument,
  GetDashboardByTokenDocument,
];

export const useUpdateAnalysis = (
  analysis: Partial<Analysis>,
  shouldRefetchQueries: boolean = true,
  refetchQueries?: DocumentNode[],
) => {
  const {
    data: { tenantID, sourceDetailsMap },
    error: dependencyError,
  } = useGetAnalysisDeps();

  const [
    updateAnalysisMutation,
    { loading, error: updateAnalysisMutationError, called },
  ] = useUpdateAnalysisMutation({
    variables: {
      ...analysis,
      id: analysis.id!,
      scope: mapToScope(analysis.scope!),
      editToken: analysis.editToken!,
      createdBy: analysis.createdBy?.id,
      tenantID: tenantID,
    },
    refetchQueries: shouldRefetchQueries
      ? [...UPDATE_ANALYSIS_REFETCH_QUERIES, ...(refetchQueries ?? [])]
      : [],
  });

  const status = apolloStatusToQueryStatus(
    loading,
    called,
    false,
    updateAnalysisMutationError,
  );

  const error = AndOfErrors([dependencyError, updateAnalysisMutationError]);

  const { showErrorNotification } = useNotificationContext();

  useEffect(() => {
    if (error) {
      showErrorNotification({
        contentComponent: `Something went wrong`,
        props: { isClosable: true },
      });
    }
  }, [error, showErrorNotification]);

  const updateAnalysis = async (args: UpdateAnalysisMutationOptions) => {
    if (!sourceDetailsMap) {
      return async () => {};
    }

    return updateAnalysisMutation(args);
  };

  return {
    isLoading: IsLoading(status),
    updateAnalysis,
    error,
  };
};
